import { v4 as uuidv4 } from 'uuid';

// Message types for inter-tab communication
export enum MessageType {
  START_RECORDING = 'startRecording',
  PAUSE_RECORDING = 'pauseRecording',
  RESUME_RECORDING = 'resumeRecording',
  END_RECORDING = 'endRecording',
  HEARTBEAT = 'heartbeat',
  RECORDING_ORPHANED = 'recordingOrphaned',
  CLAIM_OWNERSHIP = 'claimOwnership',
  NOT_AUTHORIZED = 'notAuthorized',
  RECORDING_SUBMITTED = 'recordingSubmitted',
  RECORDING_DELETED = 'recordingDeleted',
  STOP_REQUEST = 'stopRequest',
  STOP_ACKNOWLEDGED = 'stopAcknowledged',
  STOP_FAILED = 'stopFailed',
  PAUSE_REQUEST = 'pauseRequest',
  PAUSE_ACKNOWLEDGED = 'pauseAcknowledged',
  PAUSE_FAILED = 'pauseFailed',
  RESUME_REQUEST = 'resumeRequest',
  RESUME_ACKNOWLEDGED = 'resumeAcknowledged',
  RESUME_FAILED = 'resumeFailed',
}

export const STORAGE_KEYS = {
  RECORDING_OWNER_ID: 'recordingOwnerId',
  TAB_ID: 'tabId',
};

interface StopRequestMessage {
  type: MessageType.STOP_REQUEST;
  requestId: string;
  timestamp: number;
}

interface StopResponseMessage {
  type: MessageType.STOP_ACKNOWLEDGED | MessageType.STOP_FAILED;
  requestId: string;
  timestamp: number;
  error?: string;
}

interface PauseRequestMessage {
  type: MessageType.PAUSE_REQUEST;
  requestId: string;
  timestamp: number;
}

interface PauseResponseMessage {
  type: MessageType.PAUSE_ACKNOWLEDGED | MessageType.PAUSE_FAILED;
  requestId: string;
  timestamp: number;
  error?: string;
}

interface ResumeRequestMessage {
  type: MessageType.RESUME_REQUEST;
  requestId: string;
  timestamp: number;
}

interface ResumeResponseMessage {
  type: MessageType.RESUME_ACKNOWLEDGED | MessageType.RESUME_FAILED;
  requestId: string;
  timestamp: number;
  error?: string;
}

type BroadcastMessage =
  | StopRequestMessage
  | StopResponseMessage
  | PauseRequestMessage
  | PauseResponseMessage
  | ResumeRequestMessage
  | ResumeResponseMessage;

class TabCommunicator {
  private tabId: string;
  private broadcastChannel: BroadcastChannel | null = null;
  private stopRequestListeners: Map<
    string,
    { resolve: (value: { success: boolean; reason?: string }) => void; timeout: NodeJS.Timeout }
  > = new Map();
  private pauseRequestListeners: Map<
    string,
    { resolve: (value: { success: boolean; reason?: string }) => void; timeout: NodeJS.Timeout }
  > = new Map();
  private resumeRequestListeners: Map<
    string,
    { resolve: (value: { success: boolean; reason?: string }) => void; timeout: NodeJS.Timeout }
  > = new Map();
  private stopRequestCallback:
    | ((requestId: string) => Promise<{ success: boolean; error?: string }>)
    | null = null;
  private pauseRequestCallback:
    | ((requestId: string) => Promise<{ success: boolean; error?: string }>)
    | null = null;
  private resumeRequestCallback:
    | ((requestId: string) => Promise<{ success: boolean; error?: string }>)
    | null = null;
  private readonly REQUEST_TIMEOUT = 3000; // 3 seconds

  constructor() {
    // Generate or retrieve tab ID from sessionStorage
    const existingTabId = sessionStorage.getItem(STORAGE_KEYS.TAB_ID);
    this.tabId = existingTabId || uuidv4();

    if (!existingTabId) {
      sessionStorage.setItem(STORAGE_KEYS.TAB_ID, this.tabId);
    }

    // Initialize BroadcastChannel if available
    if (typeof BroadcastChannel !== 'undefined') {
      this.broadcastChannel = new BroadcastChannel('recording-control');
      this.broadcastChannel.onmessage = (event: MessageEvent<BroadcastMessage>) => {
        this.handleBroadcastMessage(event.data);
      };
    }
  }

  private async handleBroadcastMessage(message: BroadcastMessage): Promise<void> {
    // Handle responses to our stop requests
    if (message.type === MessageType.STOP_ACKNOWLEDGED || message.type === MessageType.STOP_FAILED) {
      const listener = this.stopRequestListeners.get(message.requestId);
      if (listener) {
        clearTimeout(listener.timeout);
        this.stopRequestListeners.delete(message.requestId);
        listener.resolve({
          success: message.type === MessageType.STOP_ACKNOWLEDGED,
          reason: message.type === MessageType.STOP_FAILED ? message.error || 'failed' : undefined,
        });
      }
    }

    // Handle responses to our pause requests
    if (message.type === MessageType.PAUSE_ACKNOWLEDGED || message.type === MessageType.PAUSE_FAILED) {
      const listener = this.pauseRequestListeners.get(message.requestId);
      if (listener) {
        clearTimeout(listener.timeout);
        this.pauseRequestListeners.delete(message.requestId);
        listener.resolve({
          success: message.type === MessageType.PAUSE_ACKNOWLEDGED,
          reason: message.type === MessageType.PAUSE_FAILED ? message.error || 'failed' : undefined,
        });
      }
    }

    // Handle responses to our resume requests
    if (message.type === MessageType.RESUME_ACKNOWLEDGED || message.type === MessageType.RESUME_FAILED) {
      const listener = this.resumeRequestListeners.get(message.requestId);
      if (listener) {
        clearTimeout(listener.timeout);
        this.resumeRequestListeners.delete(message.requestId);
        listener.resolve({
          success: message.type === MessageType.RESUME_ACKNOWLEDGED,
          reason: message.type === MessageType.RESUME_FAILED ? message.error || 'failed' : undefined,
        });
      }
    }

    // Handle incoming stop requests (we are the owner tab)
    if (message.type === MessageType.STOP_REQUEST && this.stopRequestCallback) {
      try {
        const result = await this.stopRequestCallback(message.requestId);
        const response: StopResponseMessage = {
          type: result.success ? MessageType.STOP_ACKNOWLEDGED : MessageType.STOP_FAILED,
          requestId: message.requestId,
          timestamp: Date.now(),
          error: result.error,
        };
        this.broadcastChannel?.postMessage(response);
      } catch (error) {
        const response: StopResponseMessage = {
          type: MessageType.STOP_FAILED,
          requestId: message.requestId,
          timestamp: Date.now(),
          error: error instanceof Error ? error.message : String(error),
        };
        this.broadcastChannel?.postMessage(response);
      }
    }

    // Handle incoming pause requests (we are the owner tab)
    if (message.type === MessageType.PAUSE_REQUEST && this.pauseRequestCallback) {
      try {
        const result = await this.pauseRequestCallback(message.requestId);
        const response: PauseResponseMessage = {
          type: result.success ? MessageType.PAUSE_ACKNOWLEDGED : MessageType.PAUSE_FAILED,
          requestId: message.requestId,
          timestamp: Date.now(),
          error: result.error,
        };
        this.broadcastChannel?.postMessage(response);
      } catch (error) {
        const response: PauseResponseMessage = {
          type: MessageType.PAUSE_FAILED,
          requestId: message.requestId,
          timestamp: Date.now(),
          error: error instanceof Error ? error.message : String(error),
        };
        this.broadcastChannel?.postMessage(response);
      }
    }

    // Handle incoming resume requests (we are the owner tab)
    if (message.type === MessageType.RESUME_REQUEST && this.resumeRequestCallback) {
      try {
        const result = await this.resumeRequestCallback(message.requestId);
        const response: ResumeResponseMessage = {
          type: result.success ? MessageType.RESUME_ACKNOWLEDGED : MessageType.RESUME_FAILED,
          requestId: message.requestId,
          timestamp: Date.now(),
          error: result.error,
        };
        this.broadcastChannel?.postMessage(response);
      } catch (error) {
        const response: ResumeResponseMessage = {
          type: MessageType.RESUME_FAILED,
          requestId: message.requestId,
          timestamp: Date.now(),
          error: error instanceof Error ? error.message : String(error),
        };
        this.broadcastChannel?.postMessage(response);
      }
    }
  }

  public getTabId(): string {
    return this.tabId;
  }

  public isRecordingOwner(): boolean {
    const ownerId = localStorage.getItem(STORAGE_KEYS.RECORDING_OWNER_ID);
    return ownerId === this.tabId;
  }

  /**
   * Broadcast a stop request to all tabs and wait for acknowledgment
   * @returns Promise that resolves with success status and optional reason
   */
  public async broadcastStopRequest(): Promise<{ success: boolean; reason?: string }> {
    if (!this.broadcastChannel) {
      // BroadcastChannel not available, fallback to failure
      return { success: false, reason: 'broadcast_unavailable' };
    }

    const requestId = uuidv4();
    const message: StopRequestMessage = {
      type: MessageType.STOP_REQUEST,
      requestId,
      timestamp: Date.now(),
    };

    return new Promise<{ success: boolean; reason?: string }>((resolve) => {
      // Set up timeout
      const timeout = setTimeout(() => {
        this.stopRequestListeners.delete(requestId);
        resolve({ success: false, reason: 'timeout' });
      }, this.REQUEST_TIMEOUT);

      // Store listener
      this.stopRequestListeners.set(requestId, { resolve, timeout });

      // Broadcast the request
      this.broadcastChannel!.postMessage(message);
    });
  }

  /**
   * Listen for stop requests and call the provided callback
   * @param callback Function to call when a stop request is received
   * @returns Cleanup function to stop listening
   */
  public listenForStopRequests(
    callback: (requestId: string) => Promise<{ success: boolean; error?: string }>,
  ): () => void {
    if (!this.broadcastChannel) {
      return () => {}; // No-op cleanup if BroadcastChannel unavailable
    }

    // Store the callback to be used in handleBroadcastMessage
    this.stopRequestCallback = callback;

    // Return cleanup function
    return () => {
      this.stopRequestCallback = null;
    };
  }

  /**
   * Broadcast a pause request to all tabs and wait for acknowledgment
   * @returns Promise that resolves with success status and optional reason
   */
  public async broadcastPauseRequest(): Promise<{ success: boolean; reason?: string }> {
    if (!this.broadcastChannel) {
      return { success: false, reason: 'broadcast_unavailable' };
    }

    const requestId = uuidv4();
    const message: PauseRequestMessage = {
      type: MessageType.PAUSE_REQUEST,
      requestId,
      timestamp: Date.now(),
    };

    return new Promise<{ success: boolean; reason?: string }>((resolve) => {
      const timeout = setTimeout(() => {
        this.pauseRequestListeners.delete(requestId);
        resolve({ success: false, reason: 'timeout' });
      }, this.REQUEST_TIMEOUT);

      this.pauseRequestListeners.set(requestId, { resolve, timeout });
      this.broadcastChannel!.postMessage(message);
    });
  }

  /**
   * Broadcast a resume request to all tabs and wait for acknowledgment
   * @returns Promise that resolves with success status and optional reason
   */
  public async broadcastResumeRequest(): Promise<{ success: boolean; reason?: string }> {
    if (!this.broadcastChannel) {
      return { success: false, reason: 'broadcast_unavailable' };
    }

    const requestId = uuidv4();
    const message: ResumeRequestMessage = {
      type: MessageType.RESUME_REQUEST,
      requestId,
      timestamp: Date.now(),
    };

    return new Promise<{ success: boolean; reason?: string }>((resolve) => {
      const timeout = setTimeout(() => {
        this.resumeRequestListeners.delete(requestId);
        resolve({ success: false, reason: 'timeout' });
      }, this.REQUEST_TIMEOUT);

      this.resumeRequestListeners.set(requestId, { resolve, timeout });
      this.broadcastChannel!.postMessage(message);
    });
  }

  /**
   * Listen for pause requests and call the provided callback
   * @param callback Function to call when a pause request is received
   * @returns Cleanup function to stop listening
   */
  public listenForPauseRequests(
    callback: (requestId: string) => Promise<{ success: boolean; error?: string }>,
  ): () => void {
    if (!this.broadcastChannel) {
      return () => {};
    }

    this.pauseRequestCallback = callback;
    return () => {
      this.pauseRequestCallback = null;
    };
  }

  /**
   * Listen for resume requests and call the provided callback
   * @param callback Function to call when a resume request is received
   * @returns Cleanup function to stop listening
   */
  public listenForResumeRequests(
    callback: (requestId: string) => Promise<{ success: boolean; error?: string }>,
  ): () => void {
    if (!this.broadcastChannel) {
      return () => {};
    }

    this.resumeRequestCallback = callback;
    return () => {
      this.resumeRequestCallback = null;
    };
  }
}

// Export a singleton instance
const tabCommunicator = new TabCommunicator();
export default tabCommunicator;
